import {
 useState
} from 'react';
import {
 Label
} from 'shared/components/ui/label';
import {
 Input
} from 'shared/components/ui/input';
import {
 Switch
} from 'shared/components/ui/switch';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Button
} from 'shared/components/ui/button';
import {
 X, Plus
} from 'lucide-react';
import StepControllers from '../components/StepControllers';
import {
 OELContext
} from '../components/OELCodeEditor';
import {
 MemoryStep
} from 'builders/builderOpcode/jsonMode/types';

const TagInput = ({
    label,
    values,
    onChange,
    placeholder
}: {
    label: string;
    values: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
}) => {
    const [inputValue, setInputValue] = useState('');

    const handleAdd = () => {
        const trimmed = inputValue.trim();
        if (trimmed && !values.includes(trimmed)) {
            onChange([...values, trimmed]);
            setInputValue('');
        }
    };

    return (
        <div className="space-y-1">
            <Label className="text-sm">{label}</Label>
            {values.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {values.map((v) => (
                        <Badge key={v} variant="secondary" className="gap-1 text-xs font-mono">
                            {v}
                            <button type="button" onClick={() => onChange(values.filter(x => x !== v))}>
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
                    placeholder={placeholder}
                    className="flex-1 text-sm"
                />
                <Button type="button" size="sm" variant="outline" onClick={handleAdd}>
                    <Plus className="h-3 w-3" />
                </Button>
            </div>
        </div>
    );
};

const V2StepMemory = ({
    step,
    onStepChange,
    oelContext,
}: {
    step: MemoryStep;
    onStepChange: (step: MemoryStep) => void;
    oelContext?: OELContext;
}) => {
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                {/* Turns */}
                <div className="space-y-1">
                    <Label>Number of Turns</Label>
                    <Input
                        type="number"
                        value={step.turns ?? 5}
                        onChange={(e) => onStepChange({
 ...step,
turns: parseInt(e.target.value, 10) || 5
})}
                        min={1}
                        max={200}
                    />
                    <p className="text-xs text-muted-foreground">1-200, default 5</p>
                </div>

                {/* Max Tokens */}
                <div className="space-y-1">
                    <Label>Max Tokens</Label>
                    <Input
                        type="number"
                        value={step.max_tokens ?? 2000}
                        onChange={(e) => onStepChange({
 ...step,
max_tokens: parseInt(e.target.value, 10) || 2000
})}
                        min={1}
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                {/* Mode */}
                <div className="space-y-1">
                    <Label>Mode</Label>
                    <Select
                        value={step.mode || 'full'}
                        onValueChange={(val) => onStepChange({
                            ...step,
                            mode: val as 'full' | 'selective'
                        })}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="full">Full</SelectItem>
                            <SelectItem value="selective">Selective</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Format */}
                <div className="space-y-1">
                    <Label>Format</Label>
                    <Select
                        value={step.format || 'block'}
                        onValueChange={(val) => onStepChange({
                            ...step,
                            format: val as 'block' | 'text'
                        })}
                    >
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="block">Block</SelectItem>
                            <SelectItem value="text">Text</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Selective mode keys */}
            {step.mode === 'selective' && (
                <div className="space-y-3 p-3 border rounded-md">
                    <TagInput
                        label="User Keys"
                        values={step.user_keys || []}
                        onChange={(vals) => onStepChange({
 ...step,
user_keys: vals
})}
                        placeholder="Add user key"
                    />
                    <TagInput
                        label="Assistant Keys"
                        values={step.assistant_keys || []}
                        onChange={(vals) => onStepChange({
 ...step,
assistant_keys: vals
})}
                        placeholder="Add assistant key"
                    />
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                {/* Include Timestamp */}
                <div className="flex items-center gap-3">
                    <Switch
                        checked={step.include_ts ?? true}
                        onCheckedChange={(checked) => onStepChange({
 ...step,
include_ts: checked
})}
                    />
                    <Label>Include Timestamps</Label>
                </div>

                {/* Max List Items */}
                <div className="space-y-1">
                    <Label>Max List Items</Label>
                    <Input
                        type="number"
                        value={step.max_list_items ?? 3}
                        onChange={(e) => onStepChange({
                            ...step,
                            max_list_items: parseInt(e.target.value, 10) || 3
                        })}
                        min={1}
                    />
                </div>
            </div>

            {/* Controllers */}
            <StepControllers
                when={step.when}
                forCtrl={step.for}
                repeatUntil={step.repeat_until}
                onWhenChange={(val) => onStepChange({
 ...step,
when: val
})}
                onForChange={(val) => onStepChange({
 ...step,
for: val
})}
                onRepeatUntilChange={(val) => onStepChange({
 ...step,
repeat_until: val
})}
                oelContext={oelContext}
            />
        </div>
    );
};

export default V2StepMemory;
